import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Download, Loader, Trash2 } from 'lucide-react'
import dayjs from 'dayjs'
import {
  getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeeEmployeeUserIdQueryKey,
  getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeesQueryKey,
  getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdLatestQueryKey,
  useDeleteApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeeEmployeeUserIdFileDocumentId,
  useGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeeEmployeeUserId,
} from '@/api/generated/company-management-user/company-management-user'
import type { PersonalDocumentEmployeeResponse, PersonalDocumentResponse } from '@/api/model'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { notify } from '@/lib/notify'
import {
  downloadDocument
} from '@/pages/service-modules/open-components/personal-documents/components/downloadDocument.ts'

interface Props {
  serviceModuleId: number
  employee: PersonalDocumentEmployeeResponse
  onClose: () => void
}

export function EmployeeDocumentsDialog({ serviceModuleId, employee, onClose }: Props) {
  const { t } = useTranslation(['service-modules-personal-documents', 'common'])
  const queryClient = useQueryClient()
  const employeeId = employee.employeeId ?? ''
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  async function handleDownload(doc: PersonalDocumentResponse) {
    if (downloadingId) return
    setDownloadingId(doc.id)
    try {
      await downloadDocument(serviceModuleId, employeeId, doc.id, doc.filename)
    } catch (error) {
      notify.apiError(error, t('personalDocuments.downloadError'))
    } finally {
      setDownloadingId(null)
    }
  }

  const { data, isLoading, isError } =
    useGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeeEmployeeUserId(
      serviceModuleId,
      employeeId, {},
      { query: { enabled: !!employeeId } },
    )
  // The generated hooks return the full Axios response — the payload lives under `.data`.
  const documents = ((data as any)?.data ?? []) as PersonalDocumentResponse[]

  const remove = useDeleteApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeeEmployeeUserIdFileDocumentId()
  const [toDelete, setToDelete] = useState<PersonalDocumentResponse | null>(null)

  async function handleDelete() {
    if (!toDelete) return
    try {
      await remove.mutateAsync({ serviceModuleId, employeeUserId: employeeId, documentId: toDelete.id })
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeeEmployeeUserIdQueryKey(serviceModuleId, employeeId),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployeesQueryKey(serviceModuleId),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdLatestQueryKey(serviceModuleId),
        }),
      ])
      notify.success(t('personalDocuments.deleteSuccess'))
    } catch (error) {
      notify.apiError(error, t('personalDocuments.deleteError'))
    } finally {
      setToDelete(null)
    }
  }

  return (
    <>
      <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('personalDocuments.employeeDialogTitle')} {employee.firstName} {employee.lastName}
            </DialogTitle>
          </DialogHeader>

          <p className="text-xs text-muted-foreground">
            {t('personalDocuments.documentsCount')}: {documents.length}
          </p>

          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
              <Loader className="size-5 animate-spin text-primary" />
              <span className="text-sm">{t('personalDocuments.loading')}</span>
            </div>
          ) : isError ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('personalDocuments.docsFailed')}</p>
          ) : documents.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('personalDocuments.noDocuments')}</p>
          ) : (
            <ul className="flex max-h-80 flex-col divide-y overflow-y-auto rounded-md border">
              {documents.map((doc) => (
                <li key={doc.id} className="flex items-center gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{doc.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('personalDocuments.uploadedAt')}: {dayjs(doc.uploadDatetime).format('DD.MM.YYYY HH:mm')}
                    </p>
                  </div>
                  <Button
                    variant="ghost" size="icon" className="size-7"
                    aria-label={t('personalDocuments.download')}
                    disabled={!!downloadingId}
                    onClick={() => handleDownload(doc)}
                  >
                    {downloadingId === doc.id
                      ? <Loader className="size-4 animate-spin" />
                      : <Download className="size-4" />
                    }
                  </Button>
                  <Button
                    variant="ghost" size="icon" className="size-7"
                    aria-label={t('buttons.delete', { ns: 'common' })}
                    onClick={() => setToDelete(doc)}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>{t('buttons.close', { ns: 'common' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title={t('personalDocuments.confirmDeleteTitle')}
        description={t('personalDocuments.confirmDeleteText')}
        confirmLabel={t('buttons.delete', { ns: 'common' })}
        confirmButtonVariant="destructive"
        isLoading={remove.isPending}
        onConfirm={handleDelete}
        onClose={() => setToDelete(null)}
      />
    </>
  )
}
